import { FormControl, FormHelperText, InputLabel } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import ReactQuill, { UnprivilegedEditor } from 'react-quill'
import { DeltaStatic } from 'quill'

import { StringUtils } from 'utils'

import { useStyles } from './styles'
import 'react-quill/dist/quill.bubble.css'

const TOOLBAR_OPTIONS = ['bold']

interface EditorInputProps {
  value?: string
  label?: string
  required?: boolean
  readOnly?: boolean
  maxLength?: number
  error?: boolean
  errorText?: string
  helperText?: React.ReactNode
  onChange?: (changes: string) => void
}

const getPlainTextLength = (editor: UnprivilegedEditor) => {
  // Quill always appends a trailing newline in getText()/getLength().
  return Math.max(0, editor.getLength() - 1)
}

const EditorInput: React.FC<EditorInputProps> = (props: EditorInputProps) => {
  const classes = useStyles()
  const maxLength = props.maxLength ?? -1
  const quillRef = useRef<ReactQuill>(null)

  const [value, setValue] = useState<string>(props.value || '')

  useEffect(() => {
    setValue(props.value || '')
  }, [props.value])

  const handleEditorContentChange = (
    content: string,
    _delta: DeltaStatic,
    _source: string,
    editor: UnprivilegedEditor,
  ) => {
    if (maxLength > 0 && getPlainTextLength(editor) > maxLength) {
      const quill = quillRef.current?.getEditor()
      if (quill) {
        const selection = quill.getSelection()
        quill.deleteText(maxLength, quill.getLength())
        if (selection) {
          quill.setSelection(Math.min(selection.index, maxLength), 0)
        }
      }
      return
    }

    setValue(content)
    props.onChange?.(content)
  }

  const showError = props.error ?? false
  const [isFocused, setIsFocused] = useState(false)
  const hasContent = StringUtils.extractTextFromHTML(value).trim().length > 0
  const shrinkLabel = isFocused || hasContent

  const handleFocus = () => setIsFocused(true)
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null
    if (nextTarget && event.currentTarget.contains(nextTarget)) return
    setIsFocused(false)
  }

  return (
    <FormControl
      fullWidth
      className={`${classes.formControl}${isFocused ? ` ${classes.focused}` : ''}`}
      error={showError}
      required={props.required}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {props.label && (
        <InputLabel required={props.required} shrink={shrinkLabel} error={showError}>
          {props.label}
        </InputLabel>
      )}
      <ReactQuill
        ref={quillRef}
        modules={{ toolbar: TOOLBAR_OPTIONS }}
        theme="bubble"
        value={value}
        onChange={handleEditorContentChange}
        readOnly={props.readOnly}
        className={classes.quillEditor}
      />
      {props.helperText ? (
        <FormHelperText>{props.helperText}</FormHelperText>
      ) : (
        showError && props.errorText && <FormHelperText>{props.errorText}</FormHelperText>
      )}
    </FormControl>
  )
}

export { EditorInput }
